import { Fragment, useMemo, useState, type MouseEvent } from 'react';
import { Link, useNavigate, useParams } from "react-router-dom";
import { useForm, FormProvider, type Resolver } from "react-hook-form";
import { zodResolver } from '@hookform/resolvers/zod';
import { isBefore, startOfDay, subMonths } from 'date-fns';

import Declaration from "@/pages/accident/declaration-sinistre/declaration";
import SelectContract from "@/components/shared/select-contract";
import Reparation from "@/pages/accident/declaration-sinistre/reparation";
import Adversaires from "@/pages/accident/declaration-sinistre/adversaires";
import Blesses from "@/pages/accident/declaration-sinistre/blesses";
import Temoins from "@/pages/accident/declaration-sinistre/temoins";
import SmallSpinner from "@/components/shared/spinners/small-spinner";
import { isEmpty } from "@/lib/collections";
import Constat from "@/pages/accident/declaration-sinistre/constat";
import Police from "@/pages/accident/declaration-sinistre/police";
import Chocs from "@/pages/accident/declaration-sinistre/chocs";
import Circonstances from "@/pages/accident/declaration-sinistre/circonstances";
import SelectionSinistre from "@/pages/accident/declaration-sinistre/selection-sinistre";
import DeclarationReparateur from "@/pages/accident/declaration-sinistre/declaration-reparateur";
import DeclarationVol from "@/pages/accident/declaration-sinistre/declaration-vol";
import ClientContactVerification from "@/pages/accident/declaration-sinistre/client-contact-verification";
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertTriangle, Info, Send } from 'lucide-react';
import {
    usePostAccident,
    usePostRejectAccident,
    useTiersAccidents,
    type DeclarationSinistreValues,
} from '@/hooks/api/use-accidents';
import { useChangeContact } from '@/hooks/api/use-amendments';
import { useSelectedContract } from '@/hooks/api/use-contracts';
import { getCodedDocuments, useContractDocuments } from '@/hooks/api/use-documents';
import { getActionBlockedReason, isActionAvailable, isMAEContract, isVAEContract } from '@/lib/contracts';
import { formatDate, toDate } from '@/lib/dates';
import { createDeclarationSinistreSchema } from '@/schemas/declaration-sinistre';
import type { DeclarationChangeEvent, DeclarationErrors } from '@/pages/accident/declaration-sinistre/types';
import { useDeclarationFleetVehicle, useDeclarationGaranties } from '@/pages/accident/declaration-sinistre/use-declaration-garanties';

const initialValues: DeclarationSinistreValues = {
    catsinorigin: '',
    adversaires: [{}],
    victimes: [{}],
    temoins: [{}],
    /* Tableaux vides comme auparavant : les champs les complètent ensuite en objets indexés */
    circonstances: [] as unknown as DeclarationSinistreValues['circonstances'],
    files: [] as unknown as DeclarationSinistreValues['files'],
    client_email: '',
    client_phone: '',
    client_password: '',
    client_contact_validated: false,
    pays_lieu_sinistre: '',
    pays: ''
};

interface DeclarationSinistreProps {
    /** Déclaration ouverte en iframe (`/frames/declaration-de-sinistre`). */
    isFrame?: boolean;
}

const DeclarationSinistre = ({ isFrame }: DeclarationSinistreProps) => {
    /* L'iframe relisait les sinistres du tiers à chaque ouverture ; l'espace les a déjà chargés */
    const accidentsQuery = useTiersAccidents(isFrame ? { refetchOnMount: 'always' } : {});
    const sinistres = accidentsQuery.data ?? [];
    const contract = useSelectedContract();
    const documents = getCodedDocuments(useContractDocuments(contract?.id).data);
    /* Sans contrat sélectionné, le choix du contrat s'affiche (`SelectContract`). */
    const declarable = contract === undefined || isActionAvailable(contract, 'declarer_sinistre');
    const blockedReason = getActionBlockedReason(contract, 'declarer_sinistre');
    const isVAE = isVAEContract(contract);
    const isMae = isMAEContract(contract);
    const garanties = useDeclarationGaranties();
    /* Flotte : la déclaration se fait depuis la fiche du véhicule, qui le passe dans l'URL (comme l'espace pro). */
    const { isFleet, vehiculeId, query: fleetVehicleQuery } = useDeclarationFleetVehicle();
    const fleetVehicleMissing = isFleet && (!vehiculeId || fleetVehicleQuery.isError);
    const souscripteur = contract?.souscripteur || {};

    const { mutate: postAccidentMutate, isPending: loading } = usePostAccident();
    const { mutate: postRejectAccidentMutate } = usePostRejectAccident();
    const { mutate: postAmendmentChangeContact, isPending: loadingChangeContact } = useChangeContact();

    const postRejectAccident = (cause: string) => postRejectAccidentMutate({
        cause,
        contractId: String(contract?.id),
    });

    const [firstCheck, setFirstCheck] = useState(false);
    const [alert, setAlert] = useState<string | false>(false);
    const [messageAlert, setMessageAlert] = useState<string | null>(null);
    const navigate = useNavigate();
    /* Lien depuis la fiche d'un véhicule de flotte : le contrat du véhicule est présélectionné. */
    const { contractId: urlContractId } = useParams();

    const validate = useMemo(
        () => zodResolver(createDeclarationSinistreSchema(contract, firstCheck)) as unknown as Resolver<DeclarationSinistreValues>,
        [contract, firstCheck],
    );

    const methods = useForm<DeclarationSinistreValues>({
        defaultValues: initialValues,
        resolver: validate,
        mode: 'onSubmit',
    });

    const { handleSubmit, formState, watch, setValue, trigger } = methods;
    const errors = formState.errors as DeclarationErrors;
    const values = watch();

    const handleChange = (e: DeclarationChangeEvent) => setValue(e.target.name, e.target.value);

    const factureReceivedAndProcessed = documents?.some(p =>
        p.code === "Q" && p.recu === true && p.traite === true
    );

    const checkFirstStep = async () => {
        const isValid = await trigger();
        if (!isValid) return;

        const dateFin = contract?.situation?.dateFin;
        if (dateFin !== "" && toDate(dateFin).getTime() < toDate(values.datesurvorigin).getTime()) {
            return setAlert("La date de survenance ne peut pas être antérieure à la date de fin de contrat.");
        } else {
            return setFirstCheck(true);
        }
    };

    const hasAccidentOpenedInLast12Months = () => {
        const threshold = startOfDay(subMonths(new Date(), 12));
        return sinistres.some((a) => {
            const opened = a.dateOuverture;
            return opened && !isBefore(startOfDay(toDate(opened)), threshold);
        });
    };

    const handleMaeSubmit = (e: MouseEvent<HTMLButtonElement>) => {
        if (!isMae) return;

        e.preventDefault();

        const currentValues = methods.getValues();

        const maeErrorMsg = 'Les garanties du contrat et les éléments de la déclaration ne permettent pas de donner suite à cette déclaration.';
        const rejectMae = (code: string) => {
            postRejectAccident(code);
            setAlert(maeErrorMsg);
        };

        const hasVI = (garanties ?? []).some(g => g.code === 'MBTHE');
        const hasCatNat = (garanties ?? []).some(g => ['MBOWN', 'MBPACB', 'MBPACB1'].includes(g.code as string));

        const isVehiculePedestrian = ['103.1', '103.3'].includes(currentValues.catsinorigin) && isEmpty(currentValues.adversaires[0]);
        const isAccidentSeul = currentValues.catsinorigin === '104.2';
        const isAccidentAnimalNonIdentifie = currentValues.catsinorigin === '104.4' && currentValues.indentify === 'F';
        const isVolOrTentative = ['10.0', '105.1'].includes(currentValues.catsinorigin);
        const isVolSansViolence = (currentValues.hasAggression === 'F' || currentValues.hasPV === 'F') && hasVI && isVolOrTentative;
        const isVolAvecViolence = currentValues.hasAggression === 'T' && currentValues.hasPV === 'T' && hasVI && currentValues.catsinorigin === '10.0';

        if (currentValues.corpoconduct === "F" && isVehiculePedestrian) return rejectMae('mae_accident_tiers');
        if (currentValues.corpoconduct === "F" && isAccidentSeul) return rejectMae('mae_accident_seul');
        if (currentValues.corpoconduct === "F" && isAccidentAnimalNonIdentifie) return rejectMae('mae_accident_animal');
        if (!hasVI && isVolOrTentative) return rejectMae('mae_vol_sans_garantie');

        if (isVolSansViolence) {
            setMessageAlert("Les informations saisies ne permettent pas la poursuite de la déclaration de sinistre. Une notification de refus sera envoyé par email au client.");
            if (currentValues.hasAggression === "F") return rejectMae("mae_vol_sans_agression");
            else if (currentValues.hasPV === "F") return rejectMae("mae_vol_sans_pv");
        }

        if (isVolAvecViolence && hasAccidentOpenedInLast12Months()) return rejectMae('mae_vol_deja_predec_12_mois');
        if ((currentValues.catsinorigin === '28.0' || currentValues.catsinorigin === '29.0') && !hasCatNat) return rejectMae('mae_cat_naturelle');

        void handleSubmit(onSubmit)();
    };

    const onSubmit = (formValues: DeclarationSinistreValues) => {
        postAccidentMutate({
            form: formValues,
            contractId: contract?.id as number,
            successFunction: () =>
                formValues.type_sinistre === 'accident'
                    ? navigate('/mes-sinistres/je-declare-un-sinistre/rdv')
                    : navigate('/'),
        });
    };

    /* Ancien loader : spinner tant que la lecture des sinistres du tiers n'est pas terminée */
    if (accidentsQuery.isPending || (isFrame && accidentsQuery.isFetching)) return <SmallSpinner />;
    if (isFleet && vehiculeId && fleetVehicleQuery.isPending) return <SmallSpinner />;

    return (
        <div className="space-y-6">
            <SelectContract isFrame={isFrame} contractId={urlContractId}>
                {/* Header */}
                <div className="space-y-2 sm:space-y-3 mt-4">
                    <h1 className="text-xl sm:text-2xl font-bold">Je déclare un sinistre</h1>
                </div>

                {isVAE || !declarable ? (
                    <Alert variant="warning" className="mt-6">
                        <AlertTriangle className="h-4 w-4" />
                        <AlertDescription>
                            Attention la déclaration de sinistre pour ce contrat n'est pas disponible.
                            {blockedReason && <> {blockedReason}</>}
                        </AlertDescription>
                    </Alert>
                ) : fleetVehicleMissing ? (
                    <Alert variant="warning" className="mt-6">
                        <AlertTriangle className="h-4 w-4" />
                        <AlertDescription>
                            Pour déclarer un sinistre sur votre flotte, ouvrez la fiche du véhicule concerné depuis votre contrat.
                            {!isFrame && contract && (
                                <> <Link to={`/mes-contrats/mon-contrat/${contract.id}`}>Voir mes véhicules</Link></>
                            )}
                        </AlertDescription>
                    </Alert>
                ) : (
                    <FormProvider {...methods}>
                        <form onSubmit={handleSubmit(onSubmit)}>
                            {!values.type_sinistre && <SelectionSinistre setValue={setValue} codeProduit={contract?.vehicule?.codeProduit} />}

                            {values.type_sinistre && (
                                <>
                                    <Declaration
                                        errors={errors}
                                        values={values}
                                        setValue={setValue}
                                        handleChange={handleChange}
                                        setFirstCheck={setFirstCheck}
                                    />
                                    {(values.catsinorigin === '10.0' || values.catsinorigin === '105.1' || values.catsinorigin === '9.0' || values.catsinorigin === '105.2') && (
                                        <DeclarationVol errors={errors} values={values} setValue={setValue} handleChange={handleChange} />
                                    )}
                                    {firstCheck && (
                                        <Fragment>
                                            {['103.1', '103.2', '103.3', '104.1', '104.3', '104.4'].includes(values.catsinorigin) && (
                                                <Constat errors={errors} values={values} setValue={setValue} handleChange={handleChange} />
                                            )}
                                            {((['103.1', '103.2', '103.3', '104.1', '104.2', '104.3', '104.4'].includes(values.catsinorigin) && values.constat_sign_tiers === 'F') || values.constat_sign_assure === 'F') && (
                                                <Police errors={errors} values={values} setValue={setValue} handleChange={handleChange} />
                                            )}
                                            {(values.indentify === 'T' || values.catsinorigin === '103.1' || values.catsinorigin === '103.2') && (
                                                <>
                                                    <Adversaires />
                                                    <Blesses />
                                                </>
                                            )}
                                            {values.temoin === 'T' && (
                                                <Temoins />
                                            )}
                                            {values.constat_accident === 'T' && (
                                                <Circonstances errors={errors} values={values} setValue={setValue} handleChange={handleChange} />
                                            )}
                                            {!isMae && ['103.1', '103.2', '103.3', '104.1', '104.2', '104.3', '104.4'].includes(values.catsinorigin) && (
                                                <Chocs errors={errors} values={values} setValue={setValue} handleChange={handleChange} />
                                            )}
                                            {!isMae && values.vehiculeDommage === 'T' && (
                                                <DeclarationReparateur errors={errors} values={values} setValue={setValue} handleChange={handleChange} />
                                            )}
                                            {values.vehicule === 'T' && values.vehiculeDommage === 'T' && values.catsinorigin !== '10.0' && (
                                                <Reparation errors={errors} values={values} setValue={setValue} handleChange={handleChange} />
                                            )}
                                        </Fragment>
                                    )}

                                    {alert && (
                                        <Alert variant="destructive" className="mt-6">
                                            <AlertTriangle className="h-4 w-4" />
                                            <AlertDescription>{messageAlert || alert}</AlertDescription>
                                        </Alert>
                                    )}

                                    {isMae && !alert && values.catsinorigin !== '104.2' && (
                                        <Alert className="mt-6">
                                            <Info className="h-4 w-4" />
                                            <AlertDescription>
                                                <p className="mb-2">Pour que votre déclaration de sinistre soit traitée, vous devez fournir les documents ci-dessous.</p>
                                                <p className="mb-2"><strong>La transmission de ces pièces est indispensable</strong> à l'étude du dossier, mais ne constitue pas une garantie de remboursement :</p>
                                                <ul className="space-y-1 text-sm ml-1">
                                                    {(values.catsinorigin === '103.1' || values.catsinorigin === '103.3' || values.catsinorigin === '104.4') && (
                                                        <>
                                                            <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5 shrink-0" />Constat amiable recto</li>
                                                            <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5 shrink-0" />Constat amiable verso</li>
                                                        </>
                                                    )}
                                                    {(values.catsinorigin === '10.0' || values.catsinorigin === '105.1') && (
                                                        <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5 shrink-0" />Procès Verbal de vol du véhicule</li>
                                                    )}
                                                    {values.catsinorigin === '28.0' && (
                                                        <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5 shrink-0" />Arrêté de Catastrophe Naturelles</li>
                                                    )}
                                                    {values.catsinorigin === '29.0' && (
                                                        <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5 shrink-0" />Arrêté de Catastrophe Technologiques</li>
                                                    )}
                                                    {!factureReceivedAndProcessed && (
                                                        <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5 shrink-0" />Facture d'achat du véhicule</li>
                                                    )}
                                                </ul>
                                            </AlertDescription>
                                        </Alert>
                                    )}

                                    {firstCheck ? (
                                        <>
                                            <ClientContactVerification
                                                souscripteur={souscripteur}
                                                postAmendmentChangeContact={postAmendmentChangeContact}
                                                loading={loadingChangeContact}
                                                values={values}
                                                setValue={setValue}
                                                handleChange={handleChange}
                                                handleBlur={handleChange}
                                                errors={errors}
                                            />
                                            <div className="mt-6">
                                                {loading ? (
                                                    <SmallSpinner />
                                                ) : (
                                                    <Button
                                                        onClick={(e) => handleMaeSubmit(e)}
                                                        className="w-full text-white cursor-pointer"
                                                        disabled={
                                                            !isMae &&
                                                            !!((!contract?.flotte && sinistres.find((s) => formatDate(s.dateSurv) === formatDate(values.datesurvorigin))) ||
                                                                (!garanties?.find((g) => g.code_famille === 'DTA') && ['104.2', '104.3', '104.5'].includes(values.catsinorigin)) ||
                                                                (!garanties?.find((g) => g.code_famille === 'VI') && ['10.0', '105.1', '9.0', '105.2', '18.0', '19.0'].includes(values.catsinorigin)) ||
                                                                !values.client_contact_validated)
                                                        }
                                                    >
                                                        <Send className="w-4 h-4" />
                                                        Envoyer ma déclaration de sinistre
                                                    </Button>
                                                )}
                                            </div>
                                        </>
                                    ) : (
                                        <div className="mt-6">
                                            <Button
                                                type="button"
                                                onClick={() => void checkFirstStep()}
                                                className="w-full text-white cursor-pointer"
                                            >
                                                Poursuivre la déclaration
                                            </Button>
                                        </div>
                                    )}
                                </>
                            )}
                        </form>
                    </FormProvider>
                )}
            </SelectContract>
        </div>
    );
};

export default DeclarationSinistre;
